import React, { useCallback, useContext, useEffect, useState } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import Popup from '../Popup'
import Text from '../Text'
import { SCREEN_SIZE } from '../../lib/Media'
import { getConfigFor } from '../../utils/configService'
import { SPLIT_FEATURES } from '../../utils/constants'
import { AccountContext } from '../AccountProvider'

export const FE_B2B_MIGRATION_GET_CONFIG_OPTIONS = {}

const GlobalStyle = createGlobalStyle`
  html {
    overflow: hidden;
  }
  .b2b-migration-popup-overlay {
    z-index: 10060 !important;
  }
`

const Card = styled.div`
  position: relative;
  background: #ffffff;
  box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.16);
  border-radius: 0.75rem;
  overflow: hidden;
  margin: 0 1rem;
  max-width: 22.5rem;
  ${SCREEN_SIZE.From.Tablet} {
    max-width: 24rem;
  }
`

const HeaderSection = styled.div`
  position: relative;
  padding: 1rem 2.75rem 1rem 1rem;
  text-align: center;
`

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
  padding: 0.125rem;
  margin: 0;
  border: none;
  background: none;
  color: #000000;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }
`

const Divider = styled.div`
  height: 0.0625rem;
  background: #eaeaea;
  margin: 0 1rem;
`

const ContentSection = styled.div`
  padding: 1rem;
  text-align: center;
`

const TitleText = styled(Text)`
  display: block;
  line-height: 1.35;
`

const BodyText = styled(Text)`
  display: block;
  margin-bottom: 1.5rem;
  line-height: 1.45;
`

const PrimaryCta = styled.a`
  display: block;
  padding: 0.875rem 1rem;
  background: #1454b8;
  border-radius: 0.375rem;
  text-align: center;
  cursor: pointer;
  text-decoration: none;
  outline: none;
  border: none;
  width: 100%;
  box-sizing: border-box;

  &:hover {
    opacity: 0.95;
  }
`

const normalizeMigrationConfig = config => {
  if (!config || typeof config !== 'object') {
    return null
  }
  const header = config.header
  const body = config.body
  const ctaText = config.cta_text ?? config.ctaText
  const ctaLink = config.cta_link ?? config.ctaLink
  return {
    header,
    body,
    cta_text: ctaText,
    cta_link: ctaLink,
  }
}

const isValidNormalizedMigrationConfig = n => {
  if (!n) {
    return false
  }
  const { header, body, cta_text: ctaText, cta_link: ctaLink } = n
  return Boolean(
    header &&
      body &&
      ctaText &&
      ctaLink &&
      typeof header === 'string' &&
      typeof body === 'string' &&
      typeof ctaText === 'string' &&
      typeof ctaLink === 'string'
  )
}

export const getB2bMigrationConfigIfEnabled = () => {
  const { isEnabled, configuration } = getConfigFor(
    SPLIT_FEATURES.FE_B2B_MIGRATION,
    FE_B2B_MIGRATION_GET_CONFIG_OPTIONS
  )
  const normalized = normalizeMigrationConfig(configuration)
  if (!isEnabled || !isValidNormalizedMigrationConfig(normalized)) {
    return null
  }
  return normalized
}

const B2bMigrationPopup = ({
  controlled,
  open: controlledOpen,
  config: controlledConfig,
  onClose: onControlledClose,
}) => {
  const { isLoggedIn } = useContext(AccountContext)
  const [open, setOpen] = useState(false)
  const [config, setConfig] = useState(null)

  const isControlled = Boolean(controlled)

  const dismiss = useCallback(() => {
    if (isControlled) {
      onControlledClose?.()
    } else {
      setOpen(false)
    }
  }, [isControlled, onControlledClose])

  useEffect(() => {
    if (isControlled || typeof window === 'undefined') {
      return
    }

    if (!isLoggedIn) {
      setOpen(false)
      setConfig(null)
      return
    }

    const normalized = getB2bMigrationConfigIfEnabled()
    if (!normalized) {
      return
    }
    setConfig(normalized)
    setOpen(true)
  }, [isLoggedIn, isControlled])

  const effectiveOpen = isControlled ? controlledOpen : open
  const effectiveConfig = isControlled ? controlledConfig : config

  const handleCtaClick = e => {
    e.preventDefault()
    if (effectiveConfig?.cta_link) {
      window.open(effectiveConfig.cta_link, '_self')
    }
    dismiss()
  }

  if (!effectiveOpen || !effectiveConfig) {
    return null
  }

  return (
    <>
      <GlobalStyle />
      <Popup
        onClose={dismiss}
        disableBackdropClosing
        data-testid="b2b-migration-popup"
        className="b2b-migration-popup-overlay"
      >
        <Card data-testid="b2b-migration-popup-card">
          <HeaderSection>
            <CloseButton
              type="button"
              onClick={dismiss}
              aria-label="Close"
              data-testid="b2b-migration-popup-close"
            >
              &times;
            </CloseButton>
            <TitleText size="xl" weight="bold" color="#333333">
              {effectiveConfig.header}
            </TitleText>
          </HeaderSection>
          <Divider data-testid="b2b-migration-popup-divider" />
          <ContentSection>
            <BodyText size="medium" color="#333333">
              {effectiveConfig.body}
            </BodyText>
            <PrimaryCta
              href={effectiveConfig.cta_link}
              onClick={handleCtaClick}
              data-testid="b2b-migration-popup-cta"
            >
              <Text size="medium" color="#ffffff" weight="bold" align="center">
                {effectiveConfig.cta_text}
              </Text>
            </PrimaryCta>
          </ContentSection>
        </Card>
      </Popup>
    </>
  )
}

export default B2bMigrationPopup
